import type { Survey } from 'types';
import type { SurveyDraft } from '@/state/surveys/useSurvey';
import { formatDateTime } from 'helpers/formatters';
import { SurveyPreview } from '../Builder/SurveyPreview';
import { SentimentSettings } from './SentimentSettings';
import { describeTrigger } from '../survey-copy';
import { useSurveyExperimentOptions } from '@/state/surveys/useSurveyExperimentOptions';

type OverviewProps = { survey: Survey };

type RowProps = {
  label: string;
  children: React.ReactNode;
  /** Names the row for the product tour, as its `data-tour` anchor. */
  tourAnchor?: string;
};

/** One line of a definition list. Label left, value right, hairline between rows. */
const Row = ({ label, children, tourAnchor }: RowProps) => (
  <div
    className="border-line flex items-baseline justify-between gap-4 border-b py-2.5 last:border-b-0"
    data-tour={tourAnchor}
  >
    <dt className="micro-label shrink-0">{label}</dt>
    <dd className="text-foreground m-0 min-w-0 text-right text-meta break-words">{children}</dd>
  </div>
);

const CHIP = 'micro-label rounded-pill border-line inline-flex border px-2 py-0.5';

/**
 * Read-only by design — every one of these is edited in the builder.
 *
 * Name, instrument and status are deliberately absent: the header above already
 * renders all three, and repeating them here is what made this screen read as a
 * wall of equally-weighted boxes. What is left is split by kind — the questions
 * are the survey's substance and get the main column, while delivery settings and
 * dates are reference material and sit in the aside, under a preview of the thing
 * a visitor actually sees.
 */
export const Overview = ({ survey }: OverviewProps) => {
  const { name, type, position, triggerConfig, theme, frequencyCap, texts, questions } = survey;
  const { experimentId, variantIds, sampleRate, targeting } = survey;
  const { cooldownDays, maxPrompts } = frequencyCap;
  // Only to name the linked experiment, so not fetched when there is none.
  const { experiments } = useSurveyExperimentOptions({
    websiteId: survey.websiteId,
    enabled: experimentId !== null,
  });
  const linked = experiments.find((experiment) => experiment.id === experimentId);
  const pageRules = targeting.filter((rule) => rule.url && rule.condition);
  const devices = targeting.flatMap((rule) => (rule.device ? [rule.device] : []));
  const variantNames = linked?.variants
    .filter((variant) => variantIds.includes(variant.id))
    .map((variant) => variant.name);
  const ordered = [...questions].sort((a, b) => a.sortOrder - b.sortOrder);

  const draft: SurveyDraft = {
    name,
    type,
    position,
    triggerConfig,
    theme,
    frequencyCap,
    texts: texts ?? {},
    targeting: targeting.map(({ url, condition, device }) => ({ url, condition, device })),
    experimentId,
    variantIds,
    sampleRate,
    questions: ordered.map((question) => ({
      kind: question.kind,
      label: question.label,
      required: question.required,
      config: question.config,
    })),
  };

  return (
    <section className="grid gap-[14px] lg:grid-cols-[minmax(0,1fr)_340px]">
      <div className="bg-panel border-line rounded-panel flex flex-col border" data-tour="survey-overview-questions">
        <div className="border-line flex items-baseline justify-between gap-3 border-b px-4 py-3">
          <h3 className="text-foreground m-0 text-body font-medium">Questions</h3>
          {ordered.length > 0 && (
            <span className="micro-label">
              {ordered.length} {ordered.length === 1 ? 'question' : 'questions'}
            </span>
          )}
        </div>

        {ordered.length === 0 ? (
          <p className="supporting px-4 py-10 text-center">No questions yet. Add them in the builder.</p>
        ) : (
          <ol className="m-0 flex list-none flex-col gap-2 p-3">
            {ordered.map((question, index) => (
              <li key={question.id} className="bg-panel2 border-line rounded-card flex items-start gap-3 border p-3">
                <span className="bg-field text-dim num mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full">
                  {index + 1}
                </span>
                <div className="min-w-0 flex-1 space-y-2">
                  <p className="text-foreground m-0 text-meta leading-snug break-words">{question.label}</p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className={CHIP}>{question.kind}</span>
                    {question.required && <span className={`${CHIP} border-sel-line text-sel`}>Required</span>}
                    {question.config.options?.map((option) => (
                      <span key={option} className={CHIP}>
                        {option}
                      </span>
                    ))}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      <aside className="bg-panel border-line rounded-panel flex flex-col border" data-tour="survey-overview-preview">
        <div className="border-line border-b px-4 py-3">
          <h3 className="text-foreground m-0 text-body font-medium">What visitors see</h3>
        </div>
        <div className="space-y-3 p-3">
          <SurveyPreview draft={draft} />

          <dl className="bg-panel2 border-line rounded-card m-0 border px-3 py-1" data-tour="survey-overview-delivery">
            <Row label="Position">{position}</Row>
            <Row label="Pages">
              {pageRules.length === 0
                ? 'Every page'
                : pageRules.map((rule) => `URL ${rule.condition} ${rule.url}`).join(', or ')}
            </Row>
            <Row label="Devices">{devices.length === 0 ? 'Every device' : devices.join(', ')}</Row>
            <Row label="Experiment">
              {experimentId === null
                ? 'Not tied to an experiment'
                : `${linked?.name ?? `Experiment ${experimentId}`}${
                    variantNames && variantNames.length > 0 ? `, only ${variantNames.join(', ')}` : ''
                  }`}
            </Row>
            <Row label="Visitors asked">
              {sampleRate === 100 ? 'All who qualify' : `${sampleRate}% of those who qualify`}
            </Row>
            <Row label="Trigger">{describeTrigger(triggerConfig)}</Row>
            {/* Answering retires the survey for that visitor outright, so the cap only
                ever governs dismissals — say so rather than let it read as a limit on
                responses. */}
            <Row label="Frequency cap">
              {`At most ${maxPrompts} prompts per visitor, ${cooldownDays} days apart after a dismissal`}
            </Row>
            <Row label="Sentiment analysis" tourAnchor="survey-overview-sentiment">
              <SentimentSettings survey={survey} />
            </Row>
            <Row label="Created">{formatDateTime(survey.createdAt ?? 0) || 'Not recorded'}</Row>
            <Row label="Started">{formatDateTime(survey.startsAt ?? 0) || 'Not started'}</Row>
            <Row label="Ended">{formatDateTime(survey.endsAt ?? 0) || 'Not ended'}</Row>
          </dl>
        </div>
      </aside>
    </section>
  );
};
